/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/pdfTextSettings.scss';

import React, {PureComponent} from 'react';
import PdfText from "../../text/pdfText";
import {
    ElementPosition,
    IndexedObject,
    PDFDisplay,
    PdfFont,
    PDFTextParams,
    PDFTextVariables,
    SelectItemShape
} from "../../../util/interfaces";
import {ColorPicker, DVRDNumberInput, DvrdSelect, fontItems, getPdfVariables} from "../../../../../index";
import classNames from 'classnames';
import IconButton, {IconButtonType} from "../buttons/iconButton";

interface Props {
    element: PdfText;
    customVariables?: PDFTextVariables;
    supportMultiPage: boolean;
}

interface State extends IndexedObject<any> {
    openVariables: keyof PDFTextVariables;
    bold: boolean;
    underline: boolean;
    italic: boolean;
    alignment?: ElementPosition;
    font: PdfFont;
    color: string;
    colorPickerActive: boolean;
    display: PDFDisplay;
}

const displayItems: SelectItemShape[] = [
    {value: PDFDisplay.ALL_PAGES, label: 'Alle pagina\'s'},
    {value: PDFDisplay.FIRST_PAGE, label: 'Alleen eerste pagina'},
    {value: PDFDisplay.LAST_PAGE, label: 'Alleen laatste pagina'},
]

export default class PdfTextSettings extends PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        const settings = props.element.getSettings();
        this.state = {
            openVariables: 'company',
            bold: settings.bold,
            underline: settings.underline,
            italic: settings.italic,
            alignment: settings.alignment,
            font: settings.font,
            color: settings.color,
            colorPickerActive: false,
            display: settings.display ?? PDFDisplay.ALL_PAGES,
        };
    }

    onChangeDisplay = (value: PDFDisplay) => {
        const {element} = this.props;
        element.setSetting('display', value, () => {
            this.setState({display: value});
        })
    }

    onCloseColorPicker = () => {
        this.setState({colorPickerActive: false});
    };

    onOpenColorPicker = () => {
        this.setState({colorPickerActive: true});
    };

    onPickColor = (close: boolean = true) => (color: string) => {
        const {element} = this.props;
        if (close)
            this.setState({colorPickerActive: false, color}, () => {
                element.setSetting('color', color)
            });
        else this.setState({color}, () => {
            element.setSetting('color', color, undefined, false);
        });
    };

    onClickVariablesTitle = (type: keyof PDFTextVariables) => () => {
        this.setState({openVariables: type});
    };

    onClickTextVariable = (variable: string) => () => {
        const {element} = this.props;
        if (element instanceof PdfText) element.insertText(`{${variable}}`);
    };

    onToggleSetting = (type: IconButtonType) => () => {
        const {element} = this.props, {bold, underline, italic} = this.state;
        let value: boolean;
        if (type === IconButtonType.BOLD) value = !bold;
        else if (type === IconButtonType.UNDERLINE) value = !underline;
        else value = !italic;
        element.setSetting(type, value, () => {
            this.setState({[type]: value});
        });
    };

    onToggleAlignment = (type: IconButtonType) => () => {
        const {alignment} = this.state, {element} = this.props;
        let nextAlignment: ElementPosition | undefined;
        if (type === IconButtonType.LEFT)
            nextAlignment = alignment === ElementPosition.LEFT ? undefined : ElementPosition.LEFT;
        else if (type === IconButtonType.CENTER)
            nextAlignment = alignment === ElementPosition.CENTER ? undefined : ElementPosition.CENTER;
        else if (type === IconButtonType.RIGHT)
            nextAlignment = alignment === ElementPosition.RIGHT ? undefined : ElementPosition.RIGHT;
        element.setSetting('alignment', nextAlignment, () => {
            this.setState({alignment: nextAlignment});
        });
    }

    renderTextVariables = () => {
        const {customVariables} = this.props, variables: PDFTextVariables = getPdfVariables(customVariables),
            {client, company, invoice} = variables;
        return (
            <div className='text-variables-container'>
                <h3 className='variables-container-title'>Tekstvariabelen</h3>
                <div className='variable-containers'>
                    {this.renderVariables('company', 'Bedrijf', company)}
                    {this.renderVariables('client', 'Klant', client)}
                    {this.renderVariables('invoice', 'Factuur', invoice)}
                </div>
            </div>
        )
    };

    renderVariables = (type: keyof PDFTextVariables, title: string, variables: IndexedObject<string>) => (
        <div className={classNames('variables-container', this.state.openVariables === type && 'open')}>
            <label className='variables-title' onClick={this.onClickVariablesTitle(type)}>{title}</label>
            <div className='variables-main-container'>
                <div className='variables'>
                    {Object.keys(variables).map((key: string, index: number) => (
                        <label key={index} className='text-variable'
                               onClick={this.onClickTextVariable(key)}>{variables[key]}</label>
                    ))}
                </div>
            </div>
        </div>
    );

    render = () => {
        const {element, supportMultiPage} = this.props,
            {bold, underline, italic, alignment, colorPickerActive, color, display} = this.state,
            settings: PDFTextParams = element.getSettings();
        return (
            <div className='pdf-text-settings'>
                {supportMultiPage && <>
                    <label className='settings-title'>Weergave</label>
                    <DvrdSelect onChange={this.onChangeDisplay} value={display} items={displayItems} unControlled/>
                </>}
                <div className='divider'/>
                <div className='text-settings'>
                    <IconButton onClick={this.onToggleSetting} active={bold} type={IconButtonType.BOLD}
                                title='Vet'/>
                    <IconButton onClick={this.onToggleSetting} active={underline} type={IconButtonType.UNDERLINE}
                                title='Onderstrepen'/>
                    <IconButton onClick={this.onToggleSetting} active={italic} type={IconButtonType.ITALIC}
                                title='Cursief'/>
                    <IconButton onClick={this.onToggleAlignment} active={alignment === ElementPosition.LEFT}
                                type={IconButtonType.LEFT} title='Links'/>
                    <IconButton onClick={this.onToggleAlignment} active={alignment === ElementPosition.CENTER}
                                type={IconButtonType.CENTER} title='Centreren'/>
                    <IconButton onClick={this.onToggleAlignment} active={alignment === ElementPosition.RIGHT}
                                type={IconButtonType.RIGHT} title='Rechts'/>
                </div>
                <div className='divider'/>
                <div className='font-settings'>
                    <DvrdSelect items={fontItems} value={settings.font} onChange={element.changeSetting('font')}
                                label='Lettertype' optionsContainerHeight='15rem' unControlled/>
                    <DVRDNumberInput value={settings.fontSize} onChange={element.changeSetting('fontSize')}
                                     label='Tekstgrootte' className='font-size'
                                     ornaments={{
                                         element: <span style={{cursor: 'text'}}>px</span>,
                                         placement: ElementPosition.RIGHT,
                                         allowPropagation: true
                                     }} wholeNumbers unControlled/>
                    <div className='settings-color-picker'>
                        <label className='picker-label'>Tekstkleur:</label>
                        <div className='color-preview' style={{backgroundColor: color}}
                             onClick={this.onOpenColorPicker}/>
                        <ColorPicker onClose={this.onCloseColorPicker} onSubmit={this.onPickColor()} closeable={false}
                                     onPreChange={this.onPickColor(false)} color={settings.color}
                                     visible={colorPickerActive} className='text-color-picker' transparent/>
                    </div>
                </div>
                <div className='divider'/>
                {!settings.disabled && this.renderTextVariables()}
            </div>
        )
    };
}